import React, { useCallback, useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '../components/button';
import Instructions from '../components/instructions';
import Settings from '../components/settings';
import SoundVideo from '../components/sound_video';
import pause from '../assets/pause.png';
import money from '../assets/money.png';
import moneySound from '../assets/money.mp3';
import arrow from '../assets/arrow.png';
import girlside from '../assets/girlside.png';
import girlback from '../assets/girlback.png';
import openDoor from '../assets/opendoor.png';
import ghost from '../assets/ghost.png';
import retry from '../assets/retry.png';
import homeButton from '../assets/homebutton.png';
import yellowStar from '../assets/yellowstar.png';
import purpleStar from '../assets/purplestar.png';
import treasure from '../assets/tressure.png';
import nextButton from '../assets/button2.png';
import footstepSound from '../assets/footstep.m4a';
import winSound from '../assets/winsound.m4a';
import loseSound from '../assets/losesound.mp3';
import lightSound from '../assets/lightsound.m4a';
import { play_sound, stop_sound } from '../audio_settings';
import { ROOM_CODE_ICONS, ROOM_SYMBOLS } from './room_puzzle_data';

const PLAYER_STEP = 3;
const MIN_PLAYER_POSITION = 5;
const MAX_PLAYER_POSITION = 90;
const MAX_CAMERA_POSITION = 76;
const SEARCHABLE_WALL_END = 72;
const LEVEL_REWARD = 100;
const MONEY_STEP = 5;
const DEFAULT_DOOR = {
  left: 91,
  top: 38
};

const getInitialCodePhase = (pathname, basePath) => {
  if (pathname === `${basePath}/win`) return 'win';
  if (pathname === `${basePath}/lose`) return 'lose';
  if (pathname === `${basePath}/video`) return 'video';
  return 'playing';
};

const RoomPuzzleLevel = ({
  basePath,
  backgroundImage,
  className = '',
  door = DEFAULT_DOOR,
  instructionsBackground,
  instructionsText,
  instructionsTitle,
  levelTime,
  moneyCount,
  onComplete,
  puzzle,
  setMoneyCount,
  setShowSettings,
  showInstructionsInitially = true,
  showSettings,
  winVideoSrc
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const startsOnGameplay = (
    location.pathname === `${basePath}/wall`
    || location.pathname === `${basePath}/gameplay`
  );
  const [showInstructions, setShowInstructions] = useState(
    showInstructionsInitially && startsOnGameplay
  );
  const [showPause, setShowPause] = useState(false);
  const [playerPosition, setPlayerPosition] = useState(16);
  const [direction, setDirection] = useState('right');
  const [isMoving, setIsMoving] = useState(false);
  const [inspectionPosition, setInspectionPosition] = useState(null);
  const [selectedCode, setSelectedCode] = useState([]);
  const [codePhase, setCodePhase] = useState(() => (
    getInitialCodePhase(location.pathname, basePath)
  ));
  const [timeLeft, setTimeLeft] = useState(levelTime);
  const [gainingMoney, setGainingMoney] = useState(false);
  const footstepAudioRef = useRef(null);
  const movementTimer = useRef(null);
  const moneyAudioRef = useRef(null);
  const moneyIntervalRef = useRef(null);
  const moneyAwardedRef = useRef(0);
  const winAudioRef = useRef(null);
  const loseAudioRef = useRef(null);
  const lightAudioRef = useRef(null);
  const isInspecting = inspectionPosition !== null;
  const showCodeScreen = location.pathname === `${basePath}/code`;
  const gameplayBlocked = showInstructions || showPause || showCodeScreen || codePhase !== 'playing';

  useEffect(() => {
    const tl = gsap.timeline();

    tl.fromTo(
      '.level_1',
      {
        opacity: 0,
        scale: 1.06
      },
      {
        opacity: 1,
        scale: 1,
        duration: 0.55,
        ease: 'power2.out'
      }
    );

    tl.fromTo(
      '.level_1_top',
      {
        opacity: 0,
        y: -34
      },
      {
        opacity: 1,
        y: 0,
        duration: 0.45,
        ease: 'back.out(1.4)'
      },
      '-=0.25'
    );

    return () => {
      tl.kill();
      window.clearTimeout(movementTimer.current);
    };
  }, []);

  const stopInspecting = useCallback(() => {
    setInspectionPosition(null);
    stop_sound(lightAudioRef.current);
  }, []);

  const movePlayer = useCallback((moveDirection) => {
    if (gameplayBlocked || isInspecting) return;

    play_sound(footstepAudioRef.current);
    setDirection(moveDirection);
    setIsMoving(true);
    setPlayerPosition((position) => {
      const amount = moveDirection === 'right' ? PLAYER_STEP : -PLAYER_STEP;
      return Math.min(MAX_PLAYER_POSITION, Math.max(MIN_PLAYER_POSITION, position + amount));
    });

    window.clearTimeout(movementTimer.current);
    movementTimer.current = window.setTimeout(() => setIsMoving(false), 260);
  }, [gameplayBlocked, isInspecting]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') movePlayer('left');
      if (event.key === 'ArrowRight') movePlayer('right');
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [movePlayer]);

  useEffect(() => {
    if (showInstructions || showPause || showSettings || codePhase !== 'playing') return;

    const timer = window.setInterval(() => {
      setTimeLeft((time) => {
        if (time === 1) {
          setCodePhase('lose');
          navigate(`${basePath}/lose`);
        }

        return Math.max(0, time - 1);
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [basePath, codePhase, navigate, showInstructions, showPause, showSettings]);

  useEffect(() => {
    if (
      codePhase !== 'win'
      || !setMoneyCount
      || showPause
      || showSettings
      || moneyAwardedRef.current >= LEVEL_REWARD
    ) return;

    const moneyAudio = moneyAudioRef.current;
    setGainingMoney(true);
    play_sound(moneyAudio, { restart: moneyAwardedRef.current === 0 });

    const moneyInterval = window.setInterval(() => {
      moneyAwardedRef.current += MONEY_STEP;
      setMoneyCount((count) => count + MONEY_STEP);

      if (moneyAwardedRef.current >= LEVEL_REWARD) {
        window.clearInterval(moneyInterval);
        setGainingMoney(false);
      }
    }, 80);
    moneyIntervalRef.current = moneyInterval;

    return () => {
      window.clearInterval(moneyInterval);
      moneyAudio.pause();
      setGainingMoney(false);
    };
  }, [codePhase, setMoneyCount, showPause, showSettings]);

  useEffect(() => {
    const resultAudio = codePhase === 'win'
      ? winAudioRef.current
      : codePhase === 'lose'
        ? loseAudioRef.current
        : null;

    if (!resultAudio) return;

    play_sound(resultAudio);
    return () => stop_sound(resultAudio);
  }, [codePhase]);

  useEffect(() => {
    const resultAudio = codePhase === 'win'
      ? winAudioRef.current
      : codePhase === 'lose'
        ? loseAudioRef.current
        : null;

    if (!resultAudio) return;

    if (showPause || showSettings) {
      resultAudio.pause();
    } else {
      play_sound(resultAudio, { restart: false });
    }
  }, [codePhase, showPause, showSettings]);

  useEffect(() => {
    const footstepAudio = footstepAudioRef.current;
    const moneyAudio = moneyAudioRef.current;
    const lightAudio = lightAudioRef.current;
    const winAudio = winAudioRef.current;
    const loseAudio = loseAudioRef.current;

    const stopDisabledSounds = (event) => {
      if (event.detail.name === 'sound' && !event.detail.value) {
        stop_sound(footstepAudio);
        stop_sound(moneyAudio);
        stop_sound(lightAudio);
        stop_sound(winAudio);
        stop_sound(loseAudio);
      }
    };

    window.addEventListener('audio_setting_change', stopDisabledSounds);

    return () => {
      window.removeEventListener('audio_setting_change', stopDisabledSounds);
      stop_sound(footstepAudio);
      stop_sound(moneyAudio);
      stop_sound(lightAudio);
      stop_sound(winAudio);
      stop_sound(loseAudio);
    };
  }, []);

  useEffect(() => {
    if (gameplayBlocked || codePhase !== 'playing') {
      setInspectionPosition(null);
      stop_sound(lightAudioRef.current);
    }
  }, [codePhase, gameplayBlocked]);

  const startInspecting = (event) => {
    if (gameplayBlocked || isMoving) return;

    const wallBounds = event.currentTarget.getBoundingClientRect();
    const heldPosition = ((event.clientX - wallBounds.left) / wallBounds.width) * SEARCHABLE_WALL_END;

    event.currentTarget.setPointerCapture(event.pointerId);
    setInspectionPosition(heldPosition);
    play_sound(lightAudioRef.current, { loop: true });
  };

  const openPause = () => {
    if (isMoving) return;

    stopInspecting();
    setShowPause(true);
  };

  const closeCodeScreen = () => {
    setSelectedCode([]);
    setCodePhase('playing');
    navigate(`${basePath}/wall`);
  };

  const openCodeScreen = () => {
    stopInspecting();
    navigate(`${basePath}/code`);
  };

  const selectCodeIcon = (icon) => {
    const nextCode = [...selectedCode, icon];
    setSelectedCode(nextCode);

    if (nextCode.length === puzzle.code.length) {
      const correct = nextCode.every((selectedIcon, index) => selectedIcon.id === puzzle.code[index]);
      const nextPhase = correct ? winVideoSrc ? 'video' : 'win' : 'lose';

      setCodePhase(nextPhase);
      navigate(`${basePath}/${nextPhase}`);
    }
  };

  const retryCode = () => {
    moneyAwardedRef.current = 0;
    setSelectedCode([]);
    setCodePhase('playing');
    setTimeLeft(levelTime);
    navigate(`${basePath}/code`);
  };

  const finishLevel = () => {
    onComplete();
  };

  const clueIsRevealed = inspectionPosition >= puzzle.clueStart && inspectionPosition <= puzzle.clueEnd;
  const lightPosition = ((inspectionPosition ?? 0) / SEARCHABLE_WALL_END) * 100;
  const cluePosition = (((puzzle.clueStart + puzzle.clueEnd) / 2) / SEARCHABLE_WALL_END) * 100;
  const cameraPosition = Math.min(MAX_CAMERA_POSITION, Math.max(0, playerPosition - 11));
  const worldClassName = `temple_world${isInspecting ? ' temple_world_inspecting' : ''}`;
  const wallClassName = `wall_search_surface${isInspecting ? ' wall_search_surface_active' : ''}`;
  const playerClassName = `temple_player temple_player_${direction}${isInspecting ? ' temple_player_back' : ''}${isMoving ? ' temple_player_moving' : ''}`;
  const timerText = `${Math.floor(timeLeft / 60)}:${String(timeLeft % 60).padStart(2, '0')}`;
  const timerIsUrgent = timeLeft > 0 && timeLeft <= 10;
  const resultActive = codePhase === 'lose' || codePhase === 'win';
  const showWinVideo = winVideoSrc && codePhase === 'video';

  return (
    <div className={`level_1 ${className}`}>
      <audio ref={footstepAudioRef} src={footstepSound} />
      <audio ref={moneyAudioRef} src={moneySound} />
      <audio ref={winAudioRef} src={winSound} />
      <audio ref={loseAudioRef} src={loseSound} />
      <audio ref={lightAudioRef} src={lightSound} />

      <div className="temple_viewport">
        <div
          className={worldClassName}
          style={{
            '--camera-offset': `-${cameraPosition}%`,
            '--inspection-position': `${inspectionPosition ?? 50}%`,
            backgroundImage: `url(${backgroundImage})`
          }}
        >
          <button
            className={wallClassName}
            type="button"
            data-no-feedback
            onPointerDown={startInspecting}
            onPointerUp={stopInspecting}
            onPointerCancel={stopInspecting}
            onLostPointerCapture={stopInspecting}
          >
            <span
              className="staff_light"
              style={{ left: `${lightPosition}%` }}
            />
            {clueIsRevealed && (
              <span
                className="hidden_hieroglyphics"
                style={{ left: `${cluePosition}%` }}
              >
                {puzzle.wallSymbols.map((symbolId) => (
                  <span key={symbolId}>{ROOM_SYMBOLS[symbolId].glyph}</span>
                ))}
              </span>
            )}
          </button>

          <div
            className={playerClassName}
            style={{ left: `${playerPosition}%` }}
          >
            <img
              src={isInspecting ? girlback : girlside}
              alt="Girl searching the temple wall"
              draggable="false"
            />
          </div>

          <button
            className="open_door_button"
            type="button"
            style={{
              left: `${door.left}%`,
              top: `${door.top}%`
            }}
            onClick={openCodeScreen}
          >
            <img src={openDoor} alt="" />
            <span>Open Door</span>
          </button>
        </div>
      </div>

      <div className="search_status">
        {isInspecting
          ? clueIsRevealed
            ? 'The staff light reveals hidden hieroglyphics!'
            : 'The staff light is active, but this wall section is empty.'
          : playerPosition > 75
            ? 'The door is ahead. Search the wall before it.'
            : 'Press and hold anywhere on the wall to activate the staff light.'}
      </div>

      <div className="movement_controls">
        <button
          type="button"
          className="movement_arrow movement_arrow_left"
          data-no-feedback
          onClick={() => movePlayer('left')}
        >
          <img src={arrow} alt="" draggable="false" />
        </button>
        <button
          type="button"
          className="movement_arrow movement_arrow_right"
          data-no-feedback
          onClick={() => movePlayer('right')}
        >
          <img src={arrow} alt="" draggable="false" />
        </button>
      </div>

      <div className="level_1_top">
        <Button className="level_1_pause" onclick={openPause}>
          <img src={pause} alt="Pause" />
        </Button>

        {codePhase === 'win' && setMoneyCount ? (
          <div className={`level_win_money ${gainingMoney ? 'level_win_money_active' : ''}`}>
            <img src={money} alt="" />
            <span>{moneyCount}</span>
          </div>
        ) : (
          <div className={`level_timer${timerIsUrgent ? ' timer_urgent' : ''}`}>
            <span>{timerText}</span>
          </div>
        )}
      </div>

      {showSettings && (
        <Settings onclose={() => setShowSettings(false)} />
      )}

      {showPause && (
        <div className="pause_overlay">
          <div className="pause_panel">
            <h1>Paused</h1>

            <Button className="pause_menu_button" onclick={() => setShowSettings(true)}>
              Settings
            </Button>

            <Button className="pause_menu_button" onclick={() => navigate('/home')}>
              Home
            </Button>

            <Button className="pause_menu_button" onclick={() => navigate('/levels')}>
              Levels
            </Button>

            <Button className="pause_menu_button" onclick={() => setShowPause(false)}>
              Resume
            </Button>
          </div>
        </div>
      )}

      {showInstructions && (
        <Instructions
          className="level_1_instructions"
          backgroundImage={instructionsBackground}
          title={instructionsTitle}
          text={instructionsText}
          onStart={() => setShowInstructions(false)}
        />
      )}

      {showCodeScreen && (
        <div className="code_screen">
          <button
            className="code_back_button"
            type="button"
            onClick={closeCodeScreen}
          >
            <img src={arrow} alt="" />
          </button>

          <p className="code_riddle">
            {puzzle.code.map((symbolId) => (
              <span key={symbolId}>{ROOM_SYMBOLS[symbolId].clue}</span>
            ))}
          </p>

          <div className="code_slots">
            {[0, 1, 2].map((slotIndex) => (
              <div
                className="code_slot"
                key={slotIndex}
              >
                {selectedCode[slotIndex] && (
                  <img src={selectedCode[slotIndex].image} alt={selectedCode[slotIndex].id} />
                )}
              </div>
            ))}
          </div>

          <div className="code_icon_buttons">
            {ROOM_CODE_ICONS.map((icon) => (
              <button
                className="code_icon_button"
                key={icon.id}
                type="button"
                disabled={codePhase !== 'playing'}
                onClick={() => selectCodeIcon(icon)}
              >
                <img src={icon.image} alt={icon.id} />
              </button>
            ))}
          </div>
        </div>
      )}

      {showWinVideo && (
        <div className="win_video_overlay">
          <SoundVideo
            className="win_video"
            src={winVideoSrc}
            autoPlay
            paused={showPause || showSettings}
            playsInline
            onEnded={() => {
              setCodePhase('win');
              navigate(`${basePath}/win`);
            }}
          />
        </div>
      )}

      {resultActive && (
        <div className="result_overlay">
          <div className="result_panel">
            {codePhase === 'lose' ? (
              <>
                <h1>You Lose!</h1>
                <img className="result_ghost" src={ghost} alt="" />

                <div className="result_actions">
                  <Button className="result_action_button" onclick={retryCode}>
                    <img src={retry} alt="Retry" />
                  </Button>
                  <Button className="result_action_button" onclick={() => navigate('/home')}>
                    <img src={homeButton} alt="Home" />
                  </Button>
                </div>
              </>
            ) : (
              <>
                <h1>Level<br />Completed!</h1>

                <div className="result_stars">
                  <img src={yellowStar} alt="" />
                  <img src={yellowStar} alt="" />
                  <img src={purpleStar} alt="" />
                </div>

                <img className="result_treasure" src={treasure} alt="" />

                <div className="result_actions">
                  <Button className="result_action_button" onclick={retryCode}>
                    <img src={retry} alt="Retry" />
                  </Button>
                  <Button className="result_action_button" onclick={finishLevel}>
                    <img src={nextButton} alt="Next level" />
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomPuzzleLevel;
